import * as Config from '../data/Config';
import User, { CustomFont } from '../data/User';
import RecordType from '../data/RecordType';
import { Resolution } from '../data/Resolution';
import { loadFile, newUUID } from '.';
import { FlyerData } from '../data/FlyerData';
import { reportIssue } from './Tracker';
import { getBaseURL } from './S3Utils';
import { TemplateCategory } from 'data/TemplateCategory';
import { ColorTheme } from 'data/ColorTheme';
import { VideoData } from 'data/VideoData';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import 'firebase/functions';
import 'firebase/storage';

const ANONYMOUS = 'anonymous';

firebase.initializeApp({
  apiKey: 'AIzaSyBPP-1Xl-WYwWa_NljVEBjhUk6P7XNwsXM',
  authDomain: 'editor.beatflyer.com',
  databaseURL: 'https://beatflyer-43167.firebaseio.com',
  projectId: 'beatflyer-43167',
  storageBucket: 'beatflyer-43167.appspot.com',
  messagingSenderId: '911750770807'
});

const db2 = firebase.initializeApp(
  {
    databaseURL: 'https://beatflyer-43167-5601d.firebaseio.com'
  },
  'db2'
);

export const loadUser = async (uid: string): Promise<User> => {
  const ref = firebase.database().ref(`/users/${uid}`);
  const snapshot = await ref.once('value');
  return snapshot.val();
};

let lastAnonymousVideoRef: any = null;

export const addUserVideo = async (
  uid: string | null,
  folder: string,
  name: string,
  layers: number,
  effects: number,
  duration: number,
  size: number
): Promise<any> => {
  const ref = firebase.database().ref(`/${folder}/`).push();
  if (!uid) {
    lastAnonymousVideoRef = ref;
  }
  await ref.set({
    uid: uid || ANONYMOUS,
    date: Date.now(),
    name,
    bucket: Config.S3_BUCKET_NAME,
    region: Config.S3_BUCKET_REGION,
    layers,
    effects,
    duration,
    size
  });
};

export const setUserColorThemes = async (
  uid: string,
  themes: ColorTheme[]
): Promise<void> => {
  const ref = firebase.database().ref(`/users/${uid}/colorThemes`);
  await ref.set(themes);
};

export const getUserCustomFonts = async (
  uid: string
): Promise<CustomFont[]> => {
  const ref = firebase.database().ref(`/users/${uid}/customFonts`);
  const snapshot = await ref.once('value');
  const data = snapshot.val() as CustomFont[] | undefined;
  return data ?? [];
};
export const setUserCustomFonts = async (
  uid: string,
  customFonts: CustomFont[]
) => {
  const ref = firebase.database().ref(`/users/${uid}/customFonts`);
  await ref.set(customFonts);
};

export const saveLastVideoToUser = async (uid: string): Promise<boolean> => {
  if (!lastAnonymousVideoRef) {
    return false;
  }
  const ref = lastAnonymousVideoRef;
  lastAnonymousVideoRef = null;
  await ref.update({
    uid
  });
  return true;
};

export const incrementVideoCounter = async (
  type: RecordType,
  uid: string | null
): Promise<User> => {
  const ref = firebase.database().ref(`/users/${uid || ANONYMOUS}`);
  const snapshot = await ref.once('value');
  const userData = snapshot.val();
  let createdVideos = userData.createdVideos || 0;
  let createdGifs = userData.createdGifs || 0;
  if (type === RecordType.GIF) {
    createdGifs++;
  } else {
    createdVideos++;
  }
  const values = {
    lastVideoCreated: Date.now(),
    createdGifs,
    createdVideos
  };
  await ref.update(values);
  return {
    ...userData,
    ...values
  };
};

export const incrementMonthlyDownloadedFlyers = async (
  uid: string | undefined
): Promise<void> => {
  if (!uid) {
    return;
  }
  const ref = firebase.database().ref(`/users/${uid}`);
  const snapshot = await ref.once('value');
  const userData = snapshot.val();
  let monthlyDownloadedFlyers = userData.monthlyDownloadedFlyers || 0;
  monthlyDownloadedFlyers++;
  await ref.update({ monthlyDownloadedFlyers });
};

export const decrementVideoCounter = async (
  uid: string = ANONYMOUS
): Promise<void> => {
  const ref = firebase.database().ref(`/users/${uid}`);
  const snapshot = await ref.once('value');
  const userData = snapshot.val();
  let createdVideos = userData.createdVideos || 0;
  if (createdVideos) {
    createdVideos--;
    await ref.update({ createdVideos });
  }
};

export const callFunction = async (
  name: string,
  data: any = null
): Promise<any> => {
  const result: any = await firebase.functions().httpsCallable(name)({
    env: process.env.NODE_ENV,
    data
  });
  return result;
};

export const addCustomResolution = async (
  uid: string,
  resolution: Resolution
) => {
  const ref = firebase.database().ref(`/users/${uid}/customResolutions`).push();
  const { name, width, height } = resolution;
  await ref.set({
    name,
    width,
    height
  });
};

export const updateTranscribeSpokenLanguage = async (
  uid: string,
  languageCode: string
) => {
  const ref = firebase.database().ref(`/users/${uid}`);
  await ref.update({
    transcribeSpokenLanguage: languageCode
  });
};

export const compressGIFCloud = async (blob: Blob): Promise<Blob | null> => {
  const storageRef = firebase.storage().ref();
  let id: string = newUUID();

  const originalFileRef = storageRef.child(`${id}.gif`);
  const compressedFileRef = storageRef.child(`${id}-compressed.gif`);

  console.log('will upload GIF to cloud');

  let uploadedOriginal = false;
  let didCompressFile = false;
  let compressedBlob: Blob | null = null;
  try {
    await originalFileRef.put(blob);
    uploadedOriginal = true;
    console.log('gif uploaded');

    const result = await callFunction('compressGIF', id);
    if (!result) {
      throw new Error('Failed compressGIF function');
    }
    didCompressFile = true;

    console.log('gif compressed');

    const url = await compressedFileRef.getDownloadURL();

    console.log('compressed gif url: ' + url);

    compressedBlob = await loadFile(url);

    console.log('compressed gif downloaded');
  } catch (e) {
    reportIssue(`Could not compress GIF in the cloud: ${e.toString()}`);
  }

  try {
    if (uploadedOriginal) {
      await originalFileRef.delete();
    }
    if (didCompressFile) {
      await compressedFileRef.delete();
    }
    console.log('gifs deleted');
  } catch (e) {
    reportIssue('Could not delete files from cloud');
  }

  return compressedBlob;
};

export const getOffer = async (
  referrer: string
): Promise<string | undefined> => {
  const offers = (await firebase.database().ref(`/offers`).once('value')).val();
  return offers[referrer];
};

export const getMusicKitToken = async (): Promise<string | undefined> => {
  const config = (await firebase.database().ref(`/config`).once('value')).val();
  return config.musicKitToken;
};

export const createNewFlyer = async (
  name: string,
  uid: string
): Promise<string> => {
  const ref = firebase.database(db2).ref(`/flyers/`).push();
  await ref.set({
    name,
    uid,
    date: Date.now()
  });
  return ref.key ?? '';
};

export const createNewTemplate = async (name: string): Promise<string> => {
  const ref = firebase.database().ref(`/templates/`).push();
  await ref.set({
    name,
    date: Date.now()
  });
  return ref.key ?? '';
};

export const saveFlyer = async (
  flyerId: string,
  uid: string,
  flyerData: FlyerData
) => {
  const ref = firebase.database(db2).ref(`/flyers/${flyerId}`);
  await ref.set({
    ...flyerData,
    uid,
    date: Date.now()
  });
};

export const updateTemplate = async (
  templateId: string,
  flyerData: FlyerData
) => {
  const ref = firebase.database().ref(`/templates/${templateId}`);
  const existingData = (await ref.once('value')).val() as FlyerData;
  await ref.set({
    ...flyerData,
    published: existingData.published || false,
    date: existingData.date,
    thumbnailUrl: existingData.thumbnailUrl,
    templateUI: existingData.templateUI || null,
    templateOptions: existingData.templateOptions || null,
    categories: existingData.categories || null,
    uid: existingData.uid || null
  });
};

export const updateTemplateVisibility = async (
  templateId: string,
  published: boolean
) => {
  const ref = firebase.database().ref(`/templates/${templateId}`);
  await ref.update({
    published
  });
};

export const increaseTemplateUsage = async (templateId: string) => {
  const ref = firebase.database().ref(`/templates/${templateId}`);
  const existingData = (await ref.once('value')).val() as FlyerData;
  const usageCount = (existingData.usageCount ?? 0) + 1;
  await ref.update({
    usageCount
  });
};

export const getFlyers = async (uid: string): Promise<FlyerData[]> => {
  const ref = firebase
    .database(db2)
    .ref('/flyers')
    .orderByChild('uid')
    .equalTo(uid);
  const snapshot = await ref.once('value');
  let dataStr = JSON.stringify(snapshot.val());
  dataStr = dataStr.split('${S3}/').join(getBaseURL());
  const data = JSON.parse(dataStr);
  const result: FlyerData[] = [];
  if (data) {
    const keys = Object.keys(data);
    keys.forEach(key => {
      const flyer = data[key];
      flyer.id = key;
      delete flyer.uploadedImages;
      result.push(flyer);
    });
  }
  return result.sort((a: FlyerData, b: FlyerData): number => {
    return b.date! - a.date!;
  });
};

export const getTemplates = async (): Promise<FlyerData[]> => {
  const ref = firebase.database().ref('/templates');
  const snapshot = await ref.once('value');
  let dataStr = JSON.stringify(snapshot.val());
  dataStr = dataStr.split('${S3}/').join(getBaseURL());
  const data = JSON.parse(dataStr);
  const templates: FlyerData[] = [];
  if (data) {
    const keys = Object.keys(data);
    keys.forEach(key => {
      const flyer = data[key];
      flyer.id = key;
      // delete flyer.uploadedImages;
      if (flyer.resolution) {
        templates.push(flyer);
      }
    });
  }
  /*const publishedTemplates = templates.filter(t => t.published);
  if (
    !isRunningInProduction() &&
    publishedTemplates.length !== templates.length
  ) {
    const unpublishedTemplates = templates.filter(t => !t.published);
    console.log('Not showing the following templates:');
    unpublishedTemplates.forEach(t => console.log(`- ${t.name}`));
  }*/
  return templates.sort((a: FlyerData, b: FlyerData): number => {
    return b.date! - a.date!;
  });
};

export const getTemplateById = async (id: string): Promise<FlyerData> => {
  const ref = firebase.database().ref(`/templates/${id}`);
  const snapshot = await ref.once('value');
  const data = snapshot.val();
  if (!data) {
    throw new Error(`Could not find template with id "${id}"`);
  }
  let dataStr = JSON.stringify(data);
  dataStr = dataStr.split('${S3}/').join(getBaseURL());
  return JSON.parse(dataStr);
};

export const getFlyer = async (
  id: string,
  isTemplate: boolean = false
): Promise<FlyerData | null> => {
  const ref = isTemplate
    ? firebase.database().ref(`/templates/${id}`)
    : firebase.database(db2).ref(`/flyers/${id}`);
  const snapshot = await ref.once('value');
  let dataStr = JSON.stringify(snapshot.val());
  dataStr = dataStr.split('${S3}/').join(getBaseURL());
  return JSON.parse(dataStr);
};

export const deleteFlyerNode = async (flyerId: string) => {
  const ref = firebase.database(db2).ref(`/flyers/${flyerId}`);
  await ref.remove();
};

export const renameFlyer = async (flyerId: string, newName: string) => {
  const ref = firebase.database(db2).ref(`/flyers/${flyerId}`);
  await ref.update({
    name: newName
  });
};

let examples: FlyerData[] | undefined = undefined;

export const getExamples = async (): Promise<FlyerData[]> => {
  if (examples) {
    return examples;
  }
  const result: FlyerData[] = [];
  const ref = firebase.database().ref(`/examples`);
  const snapshot = await ref.once('value');
  let dataStr = JSON.stringify(snapshot.val());
  dataStr = dataStr.split('${S3}/').join(getBaseURL());
  const data = JSON.parse(dataStr);
  const ids = Object.keys(data);
  for (const id of ids) {
    const example = data[id] as FlyerData;
    example.id = id;
    result.push(example);
  }
  examples = result.sort((a, b) => {
    return (a as any).index - (b as any).index;
  });
  return examples;
};

export interface PatternData {
  full: string;
  small: string;
}

let patterns: PatternData[] | undefined = undefined;

export const getPatterns = async (): Promise<PatternData[]> => {
  if (patterns) {
    return patterns;
  }
  const result: PatternData[] = [];
  const ref = firebase.database().ref(`/gradients`);
  const snapshot = await ref.once('value');
  let dataStr = JSON.stringify(snapshot.val());
  dataStr = dataStr
    .split('${S3}')
    .join('https://beatflyer-test.s3-eu-west-1.amazonaws.com');
  const data = JSON.parse(dataStr);
  const ids = Object.keys(data);
  for (const id of ids) {
    const pattern = data[id] as PatternData;
    result.push(pattern);
  }
  patterns = result;
  return patterns;
};

export interface GiphyUser {
  avatar_url: string;
  display_name: string;
  is_verified: boolean;
  profile_url: string;
}

export interface StickerData {
  url: string;
  thumbnailUrl: string;
  keywords?: string;
  giphyUser?: GiphyUser;
}

let stickers: StickerData[];

export const getStickers = async (): Promise<StickerData[]> => {
  if (stickers) {
    return stickers;
  }
  const result: StickerData[] = [];
  const ref = firebase.database().ref(`/stickers`);
  const snapshot = await ref.once('value');
  let dataStr = JSON.stringify(snapshot.val());
  dataStr = dataStr
    .split('${S3}')
    .join('https://beatflyer-test.s3-eu-west-1.amazonaws.com');
  const data = JSON.parse(dataStr);
  const ids = Object.keys(data);
  for (const id of ids) {
    const sticker = data[id] as StickerData;
    if (!sticker.thumbnailUrl) {
      sticker.thumbnailUrl = sticker.url;
    }
    result.push(sticker);
  }
  stickers = result;
  return stickers;
};

export const isAppSumoCodeValid = async (code: string): Promise<boolean> => {
  const ref = firebase.database().ref(`/appsumo-codes/${code}`);
  const snapshot = await ref.once('value');
  const value = snapshot.val();
  if (value === null) {
    await callFunction('notify', {
      title: 'Attempt to use invalid code',
      message: `Code tried: ${code}`
    });
  }
  return value === 'available';
};

export const useAppSumoCode = async (
  code: string,
  uid: string,
  email: string
) => {
  const ref = firebase.database().ref(`/appsumo-codes/${code}`);
  await ref.set({
    uid,
    email,
    date: new Date().toString()
  });
};

export const getImageLibrary = async (
  libraryName: string
): Promise<string[]> => {
  const ref = firebase.database().ref(`/library/${libraryName}`);
  const snapshot = await ref.once('value');
  return snapshot.val();
};

export const getTemplatesCategories = async (): Promise<TemplateCategory[]> => {
  const ref = firebase.database().ref(`/templatesCategories`);
  const snapshot = await ref.once('value');
  return snapshot.val();
};

// export const addSizeToUserVideo = async (size: number, videoRef: any) => {
//   await videoRef.update({ size });
// };

export const getVideos = async (uid: string): Promise<VideoData[]> => {
  const result: VideoData[] = [];
  const data = (
    await firebase
      .database()
      .ref('/videos')
      .orderByChild('uid')
      .equalTo(uid)
      .once('value')
  ).val();
  if (data) {
    const keys = Object.keys(data);
    keys.forEach(key => {
      const flyer = data[key];
      flyer.id = key;
      delete flyer.uploadedImages;
      result.push(flyer);
    });
  }
  return result.sort((a: VideoData, b: VideoData): number => {
    return b.date - a.date;
  });
};

export const deleteVideoNode = async (videoId: string) => {
  const ref = firebase.database().ref(`/videos/${videoId}`);
  await ref.remove();
};

// export const moveVideos = async (data: any, uid: string): Promise<void> => {
//   const keys = Object.keys(data);
//   await asyncForEach(keys, async (key: string) => {
//     const videoData = data[key];
//     videoData.uid = uid;
//     const initialVideoRef = firebase
//       .database()
//       .ref(`/users/${uid}/videos/${key}`);
//     const newVideoRef = firebase
//       .database()
//       .ref(`/videos`)
//       .push();
//     await newVideoRef.set(videoData);
//     await initialVideoRef.remove();
//   });
// };

// export const moveAllVideos = async (): Promise<void> => {
//   const users = (await firebase
//     .database()
//     .ref('/users')
//     .once('value')).val();
//   const keys = Object.keys(users);
//   console.log(users);
//   await asyncForEach(keys, async (key: string) => {
//     const userData = users[key];
//     if (userData.videos) {
//       await moveVideos(userData.videos, key);
//     }
//   });
// };

export const sendMeEmail = async (
  title: string,
  message: string
): Promise<void> => {
  await callFunction('notify', {
    title,
    message
  });
};

export const skipSegmentationSurvey = async (uid: string) => {
  const ref = firebase.database().ref(`/users/${uid}/segmentationSurvey`);
  await ref.set('skip');
};

export const submitSegmentationSurvey = async (
  uid: string,
  values: Record<string, string>
) => {
  const ref = firebase.database().ref(`/users/${uid}/segmentationSurvey`);
  await ref.set(values);
};



// WEBPACK FOOTER //
// ./src/utils/FirebaseUtils.ts